import { z } from 'zod';

export const ALLOWANCE_TYPES = ['fixed', 'percent_of_basic', 'percent_of_gross'] as const;
export const allowanceTypeEnum = z.enum(ALLOWANCE_TYPES);
export type AllowanceType = z.infer<typeof allowanceTypeEnum>;

const allowanceSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(60),
  type: allowanceTypeEnum.default('fixed'),
  value: z.coerce.number().min(0).default(0),
});

/**
 * A reusable pay template — CTC breaks down into these percentages/fixed
 * amounts, and PF/ESI/PT/TDS get computed from them. Ported from
 * Office_project's `SalaryStructure` model.
 *
 * `taxRegime` is carried over from the source model but isn't actually read
 * by the calculation engine yet — only the new-regime slabs are implemented
 * (see `payrollEngine.ts`). Kept here for schema/UI compatibility, not
 * because switching it does anything yet.
 */
export const createSalaryStructureSchema = z.object({
  name: z.string().trim().min(2, 'Name must be at least 2 characters').max(120),
  description: z.string().trim().max(500).optional().or(z.literal('')),
  grade: z.string().trim().max(60).optional().or(z.literal('')),

  basicPercent: z.coerce.number().min(0).max(100).default(40),
  hraPercent: z.coerce.number().min(0).max(100).default(50),
  daPercent: z.coerce.number().min(0).max(100).default(0),
  specialAllowancePercent: z.coerce.number().min(0).max(100).default(0),
  conveyanceAllowance: z.coerce.number().min(0).default(1600),
  medicalAllowance: z.coerce.number().min(0).default(1250),
  otherAllowances: z.array(allowanceSchema).default([]),

  pfEnabled: z.boolean().default(true),
  pfRate: z.coerce.number().min(0).max(100).default(12),
  pfCap: z.coerce.number().min(0).default(15000),
  esiEnabled: z.boolean().default(true),
  esiGrossLimit: z.coerce.number().min(0).default(21000),
  esiRate: z.coerce.number().min(0).max(100).default(0.75),
  esiEmployerRate: z.coerce.number().min(0).max(100).default(3.25),
  professionalTax: z.coerce.number().min(0).default(200),
  tdsEnabled: z.boolean().default(true),
  taxRegime: z.enum(['new', 'old']).default('new'),

  overtimeEnabled: z.boolean().default(false),
  overtimeRate: z.coerce.number().min(0).default(2),
});

export type CreateSalaryStructureInput = z.infer<typeof createSalaryStructureSchema>;

export const updateSalaryStructureSchema = createSalaryStructureSchema.partial();
export type UpdateSalaryStructureInput = z.infer<typeof updateSalaryStructureSchema>;
